import React, { createContext, useState } from 'react';
import { T, Version, UrlParams } from '../enums';
import { getUrlParam } from '../utils';

interface ThemeContextType {
  theme: T;
  isDynamicColors: boolean;
  version: Version;
  toggleTheme: (themeURL?: string) => void;
  updateDynamicColors: (isDynamicColors: boolean) => void;
  updateVersion: (version: Version) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: T.LIGHT,
  isDynamicColors: true,
  version: Version.V2,
  toggleTheme: () => {},
  updateDynamicColors: () => {},
  updateVersion: () => {},
});

const getInitialTheme = (): T => {
  const themeURL = getUrlParam(UrlParams.THEME);
  if (themeURL && (themeURL === 'light' || themeURL === 'dark')) {
    return themeURL as T;
  }
  return T.LIGHT;
};

const getInitialDynamicColors = (): boolean => {
  const dynamicColorsURL = getUrlParam(UrlParams.DYNAMIC_COLORS);
  if (dynamicColorsURL) {
    return dynamicColorsURL === 'true';
  }
  return true;
};

const getInitialVersion = (): Version => {
  const versionURL = getUrlParam(UrlParams.VERSION);
  if (versionURL && Object.values(Version).includes(versionURL as Version)) {
    return versionURL as Version;
  }
  return Version.V2;
};

export interface ThemeProviderProps {
  children: React.ReactNode;
  /** When set (e.g. in Storybook), theme is driven by this prop and kept in sync. Otherwise theme comes from URL. */
  initialTheme?: T;
}

export const ThemeProvider = ({ children, initialTheme }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<T>(() => initialTheme ?? getInitialTheme());
  const [isDynamicColors, setDynamicColors] = useState<boolean>(getInitialDynamicColors);
  const [version, setVersion] = useState<Version>(getInitialVersion);
  const isV2 = version === Version.V2;

  const [prevInitialTheme, setPrevInitialTheme] = useState(initialTheme);

  if (initialTheme !== prevInitialTheme) {
    setPrevInitialTheme(initialTheme);

    if (initialTheme !== undefined) {
      setTheme(initialTheme);
    }
  }

  const [prevIsV2, setPrevIsV2] = useState(isV2);

  if (isV2 !== prevIsV2) {
    setPrevIsV2(isV2);

    if (isV2) {
      setDynamicColors(true);
    }
  }

  const toggleTheme = (themeName?: string) => {
    setTheme((prevTheme) => {
      if (themeName) {
        return themeName as T;
      }
      const newTheme = prevTheme === T.LIGHT ? T.DARK : T.LIGHT;
      return newTheme;
    });
  };

  const updateDynamicColors = (value: boolean) => {
    if (isV2) {
      setDynamicColors(true);
    } else {
      setDynamicColors(value);
    }
  };

  const updateVersion = (newVersion: Version) => {
    setVersion(newVersion);
    if (newVersion === Version.V2) {
      setDynamicColors(true);
    }
  };

  return (
    <ThemeContext.Provider
      value={{ theme, isDynamicColors, version, toggleTheme, updateDynamicColors, updateVersion }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
export { ThemeContext };
